import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { formatBytes } from '../../../shared/attachments.js'
import { modeLabel } from '../../../shared/catalog.js'
import { parseQuestion } from '../../../shared/mentions.js'
import { describeError } from '../../lib/chat-errors.js'
import { Markdown } from '../../lib/markdown.jsx'
import { Icon } from '../icons/Icon.jsx'
import { MentionChip } from '../ui/MentionChip.jsx'
import { WIDGETS, WidgetBoundary } from './widgets/index.js'
import s from './answer.module.css'

/** Files sent with a question: image thumbnails and file chips; each opens the file. */
function SentAttachments({ attachments }) {
  return (
    <div className={s.sentFiles}>
      {attachments.map((a, i) =>
        a.kind === 'image' ? (
          <a key={a.id ?? i} href={a.url} target="_blank" rel="noreferrer" className={s.sentImage} title={a.name}>
            <img src={a.url} alt={a.name} loading="lazy" />
          </a>
        ) : (
          <a key={a.id ?? i} href={a.url} target="_blank" rel="noreferrer" className={s.sentFile} title={a.name}>
            <Icon name="file" size={16} strokeWidth={1.6} />
            <span className={s.sentFileName}>{a.name}</span>
            {a.size ? <span className={s.sentFileSize}>{formatBytes(a.size)}</span> : null}
          </a>
        ),
      )}
    </div>
  )
}

export function UserMessage({ text, attachments = [], systemsMap, wide = false }) {
  const { segments } = parseQuestion(text)
  // Mentions typed at the start render first, like the design; mid-sentence
  // mentions stay where they were written.
  return (
    <div className={s.userRow}>
      <div className={s.bubble} style={{ maxWidth: wide ? 620 : 600 }}>
        {attachments.length ? <SentAttachments attachments={attachments} /> : null}
        {segments.map((segment, i) =>
          segment.type === 'mention' ? (
            <MentionChip key={i} system={systemsMap.get(segment.slug)} label={segment.label} variant="message" />
          ) : (
            <span key={i}>{segment.value}</span>
          ),
        )}
      </div>
    </div>
  )
}

/** Answer header: the mode. */
export function AnswerHeader({ meta }) {
  const mode = meta?.mode ? (modeLabel(meta.mode) ?? meta.mode) : null
  if (!mode) return null
  return (
    <div className={s.answerHeader}>
      <div className={s.answerMode}>
        Mode
        <span className={s.modePill}>{mode}</span>
      </div>
    </div>
  )
}

export function FeedbackRow({ rating, onRate, onCopy, copied }) {
  return (
    <div className={s.feedback}>
      <button
        type="button"
        className={`icon-btn ${rating === 1 ? s.feedbackOn : ''}`}
        aria-label="Helpful"
        aria-pressed={rating === 1}
        onClick={() => onRate(rating === 1 ? 0 : 1)}
      >
        <Icon name="thumb-up" size={16} strokeWidth={1.5} />
      </button>
      <button
        type="button"
        className={`icon-btn ${rating === -1 ? s.feedbackOn : ''}`}
        aria-label="Not helpful"
        aria-pressed={rating === -1}
        onClick={() => onRate(rating === -1 ? 0 : -1)}
      >
        <Icon name="thumb-down" size={16} strokeWidth={1.5} />
      </button>
      <button type="button" className="icon-btn" aria-label={copied ? 'Copied' : 'Copy answer'} onClick={onCopy}>
        <Icon name={copied ? 'check' : 'copy'} size={16} strokeWidth={1.5} />
      </button>
    </div>
  )
}

// ---- Progress while an answer streams ------------------------------------------

const WIDGET_NAMES = {
  show_pipe_flow: 'the pipe flow',
  show_handoff: 'the handoff timeline',
  show_endpoint: 'the request builder',
  show_fields: 'the field table',
  show_gotchas: 'the list of things to watch for',
  show_questionnaire: 'the client questionnaire',
  show_checklist: 'the checklist',
  show_email_draft: 'the email draft',
  ask_clarification: 'a question for you',
  pick_scope: 'the scope picker',
}

/** "references/pipe-playbooks.md" → "pipe playbooks"; SKILL.md → "the skill overview". */
function fileLabel(file = '') {
  const name = String(file).split('/').pop().replace(/\.md$/i, '')
  if (!name || /^skill$/i.test(name)) return 'the skill overview'
  return name.replace(/[-_]+/g, ' ')
}

function statusLabel(answer, systemsMap) {
  const step = answer.activity.findLast?.((a) => !a.done)
  if (step) {
    const system = systemsMap.get(step.input?.system)?.name
    return step.name === 'search_skill'
      ? `Searching ${system ?? 'the skills'} for “${step.input?.query ?? ''}”`
      : `Reading ${fileLabel(step.input?.file)}${system ? ` in ${system}` : ''}`
  }
  const last = answer.blocks[answer.blocks.length - 1]
  if (last && last.kind !== 'text' && !last.complete) return `Building ${WIDGET_NAMES[last.kind] ?? 'a card'}`
  if (answer.writing) return 'Writing the answer'
  return answer.blocks.length || answer.activity.length || answer.meta ? 'Thinking' : 'Reading your question'
}

function useElapsed(startedAt) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  return Math.max(0, Math.floor((now - (startedAt ?? now)) / 1000))
}

/** One line under a streaming answer: what it is doing now, and for how long. */
function AnswerStatus({ answer, systemsMap, startedAt }) {
  const seconds = useElapsed(startedAt)
  const label = statusLabel(answer, systemsMap)
  return (
    <div className={s.status}>
      <span className={s.dots} aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <span role="status" aria-live="polite">
        {label}…
      </span>
      {seconds >= 5 ? <span className={s.statusTime}>{seconds}s</span> : null}
      {seconds >= 40 ? <span className={s.statusHint}>Long answers can take a minute.</span> : null}
    </div>
  )
}

// ---- Errors and stops -----------------------------------------------------------

/**
 * What went wrong, in plain words, with the one action that helps.
 * `error` is an Error, an ApiError or a stored error string; `partial` means
 * part of the answer is already shown above.
 */
export function ErrorNotice({ error, partial = false, onRetry, readOnly = false }) {
  const info = describeError(typeof error === 'string' ? new Error(error) : error)
  if (!info) return null
  const next = typeof window === 'undefined' ? '/' : `${window.location.pathname}${window.location.search}`
  return (
    <div className={s.errorCard} role="alert">
      <span className={s.errorIcon}>
        <Icon name="warning" size={18} strokeWidth={1.7} />
      </span>
      <div className={s.errorBody}>
        <span className={s.errorTitle}>{partial ? `${info.title}. The answer above is incomplete.` : info.title}</span>
        <span className={s.errorText}>{info.message}</span>
        {info.details ? (
          <details className={s.errorDetails}>
            <summary>Details</summary>
            <code>{info.details}</code>
          </details>
        ) : null}
      </div>
      {readOnly ? null : (
        <div className={s.errorActions}>
          {info.action === 'retry' && onRetry ? (
            <button type="button" className="btn btn-ghost" onClick={onRetry}>
              <Icon name="refresh" size={14} strokeWidth={1.7} />
              Try again
            </button>
          ) : null}
          {info.action === 'signin' ? (
            <Link to="/login" search={{ next }} className="btn btn-ghost">
              Sign in again
            </Link>
          ) : null}
          {info.action === 'new' ? (
            <Link to="/new" className="btn btn-ghost">
              New question
            </Link>
          ) : null}
        </div>
      )}
    </div>
  )
}

/** The user pressed Stop (or an earlier answer was stopped). */
export function StoppedNote({ onRetry, readOnly = false }) {
  return (
    <div className={s.stopped}>
      <Icon name="info" size={15} strokeWidth={1.7} />
      <span>{readOnly ? 'This answer was stopped before it finished.' : 'You stopped this answer.'}</span>
      {onRetry && !readOnly ? (
        <button type="button" className={s.stoppedRetry} onClick={onRetry}>
          Answer again
        </button>
      ) : null}
    </div>
  )
}

/**
 * Prose, or a widget from the registry. `reply` is the user's next message
 * (it answers an input widget); `canAnswer` is true only on the newest,
 * finished answer.
 */
function Block({ block, reply, canAnswer }) {
  if (block.kind === 'text') return <Markdown text={block.text} className={s.prose} codeBlockClassName={s.code} />
  const widget = WIDGETS[block.kind]
  if (!widget) return null
  const { Component } = widget
  return (
    <WidgetBoundary>
      <Component input={block.input ?? {}} block={block} reply={reply} canAnswer={canAnswer} />
    </WidgetBoundary>
  )
}

/** Suggested next questions in plain words; clicking one asks it. */
function Followups({ questions, onAsk }) {
  return (
    <div className={s.followups} aria-label="Suggested questions">
      <span className="section-label">Ask next</span>
      <div className={s.followupList}>
        {questions.map((q) => (
          <button key={q} type="button" className={s.followup} onClick={() => onAsk(q)}>
            <Icon name="arrow-right" size={14} strokeWidth={1.7} />
            <span>{q}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

/** One answer. `answer` comes from buildAnswer(message). */
export function AssistantMessage({
  answer,
  systemsMap,
  streaming,
  error,
  onRetry,
  feedback,
  onRate,
  onCopy,
  copied,
  onAsk,
  reply = null,
  stopped = false,
  startedAt = null,
  readOnly = false,
}) {
  const hasContent = answer.blocks.length > 0
  const canAnswer = Boolean(onAsk) && !streaming && !readOnly && !reply
  // An answer that ends on a question to the user offers no follow-ups.
  const awaitingUser = answer.blocks.some((b) => WIDGETS[b.kind]?.kind === 'input')
  return (
    <article aria-label="Answer" aria-busy={streaming} className={s.answer}>
      <AnswerHeader meta={answer.meta} />
      {answer.blocks.map((block) => (
        <Block key={block.key} block={block} reply={reply} canAnswer={canAnswer} />
      ))}
      {streaming ? <AnswerStatus answer={answer} systemsMap={systemsMap} startedAt={startedAt} /> : null}
      {error && !streaming ? <ErrorNotice error={error} partial={hasContent} onRetry={onRetry} readOnly={readOnly} /> : null}
      {stopped && !error && !streaming ? <StoppedNote onRetry={onRetry} readOnly={readOnly} /> : null}
      {!streaming && !readOnly && onAsk && !awaitingUser && answer.followups?.length ? (
        <Followups questions={answer.followups} onAsk={onAsk} />
      ) : null}
      {!streaming && hasContent && !readOnly ? (
        <FeedbackRow rating={feedback} onRate={onRate} onCopy={onCopy} copied={copied} />
      ) : null}
    </article>
  )
}

